import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from 'src/app/services/multilingual/translate.service';
import {
  OrderDeliveries,
  OrderDeliveryItem,
  OrderStatus
} from '../types';

@Component({
  selector: 'dashboard-order-deliveries',
  templateUrl: './deliveries.component.html',
  styleUrls: ['./deliveries.component.scss']
})
export class DashboardOrderDeliveriesComponent implements OnChanges {
  @Input() deliveries: OrderDeliveries = {
    summary: { running: 0, upcoming: 0, onCall: 0, overdue: 0 },
    items: []
  };

  readonly OrderStatus = OrderStatus;
  activeTabId: string = 'all';

  constructor(
    private TranslateService: TranslateService,
    private Router: Router
  ) { }

  get statusTabs(): Array<{
    id: string;
    status: OrderDeliveryItem['status'] | null;
    label: string;
    count: number;
    className: string;
  }> {
    return [
      {
        id: 'all',
        status: null,
        label: this.TranslateService.GetTranslation('ui.all'),
        count: this.deliveries.items.length,
        className: 'all'
      },
      {
        id: 'running',
        status: OrderStatus.running,
        label: this.TranslateService.GetTranslation('ui.running'),
        count: this.deliveries.summary.running,
        className: 'running'
      },
      {
        id: 'upcoming',
        status: OrderStatus.upcoming,
        label: this.TranslateService.GetTranslation('ui.upcoming'),
        count: this.deliveries.summary.upcoming,
        className: 'upcoming'
      },
      {
        id: 'on-call',
        status: OrderStatus.onCall,
        label: this.TranslateService.GetTranslation('order.on-call'),
        count: this.deliveries.summary.onCall,
        className: 'on-call'
      },
      {
        id: 'overdue',
        status: OrderStatus.laggingBehind,
        label: this.TranslateService.GetTranslation('ui.lagging-behind'),
        count: this.deliveries.summary.overdue,
        className: 'overdue'
      }
    ];
  }

  get activeTab() {
    return this.statusTabs.find(tab => tab.id === this.activeTabId) ?? this.statusTabs[0];
  }

  get activeItems(): OrderDeliveryItem[] {
    if(this.activeTab.status === null)
      return this.sortItems(this.deliveries.items);

    return this.getItemsByStatus(this.activeTab.status);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(!changes.deliveries)
      return;

    if(this.statusTabs.some(tab => tab.id === this.activeTabId))
      return;

    this.activeTabId = 'all';
  }

  selectTab(tabId: string): void {
    this.activeTabId = tabId;
  }

  getItemsByStatus(status: OrderDeliveryItem['status']): OrderDeliveryItem[] {
    return this.sortItems(this.deliveries.items.filter(item => item.status === status));
  }

  private sortItems(items: OrderDeliveryItem[]): OrderDeliveryItem[] {
    return [...items].sort((left, right) => left.time.localeCompare(right.time));
  }

  getStatusLabel(status: OrderDeliveryItem['status']): string {
    return this.statusTabs.find(tab => tab.status === status)?.label ?? '';
  }

  getStatusClass(status: OrderDeliveryItem['status']): string {
    return this.statusTabs.find(tab => tab.status === status)?.className ?? '';
  }

  getProgressPercentage(item: OrderDeliveryItem): number {
    if(item.plannedAmount <= 0)
      return 0;
    return Math.min(100, Math.round(item.deliveredAmount / item.plannedAmount * 100));
  }

  trackByOrderId(_index: number, item: OrderDeliveryItem): number {
    return item.orderId;
  }

  toPlanning() {
    this.Router.navigateByUrl('planning')
  }
}
